import AvvParser from "~/features/parser"
import type { V } from "./types"

const FIELD_REGEXP = /(\{\{[^\|]+?\|[^\}]*?\}\})/
const FIELD_REGEXP_WITH_CAPTURES = /\{\{([^\|]+?)\|([^\}]*?)\}\}/

const SPAN_REGEXP = /(<span data-variable="[^"]+?" data-default="[^"]*?".+?<\/span>)/
const SPAN_REGEXP_WITH_CAPTURES = /<span data-variable="([^"]+?)" data-default="([^"]*?)".+?<\/span>/

const AUTONAME_REGEXP = /(\{\{'Att_default': ?\['[^']*?', '[^']*?'\]\}\})/
const AUTONAME_REGEXP_WITH_CAPTURES = /\{\{'Att_default': \['([^']*?)', '([^']*?)'\]\}\}/

// This is duplicated on purpose!
const encodeHTML = (html: string) => html.replaceAll('&', '&amp;').replaceAll('>', '&gt;').replaceAll('<', '&lt;').replaceAll('\u00A0', '&nbsp;')
const decodeHTML = (html: string) => html.replaceAll('&nbsp;', '\u00A0').replaceAll('&gt;', '>').replaceAll('&lt;', '<').replaceAll('&amp;', '&')

function createField (value: string, defaultValue: string) {
  return `{{${value}|${defaultValue}}}`
}

export function createSpan(value: string, defaultValue: string, variables: V.SmartInput.Variable[], skipEncoding = false) {
  if (!skipEncoding) {
    value = AvvParser.encode(value)
    defaultValue = AvvParser.encode(defaultValue)
  }

  const element = document.createElement('span')
  element.setAttribute('data-variable', value)
  element.setAttribute('data-default', defaultValue)
  element.setAttribute('contenteditable', 'false')
  element.setAttribute('class', 'px-2.5 py-1 bg-neutral-200 text-neutral-550 rounded-md with-contenteditable-guards')

  const decodedValue = AvvParser.decode(value)

  const label = variables.find((variable) => variable.value === decodedValue)?.label ?? decodedValue
  element.innerText = defaultValue ? `${label} - ${AvvParser.decode(defaultValue)}` : label

  return element
}

export function measureSmartStringLength(smartString: string | null | undefined) {
  return (smartString ?? '').split(FIELD_REGEXP).reduce((acc, item) => acc + (FIELD_REGEXP.test(item) ? 1 : AvvParser.decode(item).length), 0)
}

export function convertSmartStringToHtml(smartString: string | null | undefined, variables: V.SmartInput.Variable[]) {
  const html = (smartString ?? '').split(FIELD_REGEXP).map((item) => {
    if (FIELD_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(FIELD_REGEXP_WITH_CAPTURES)!
      return createSpan(value, defaultValue, variables, true).outerHTML
    } else {
      return encodeHTML(AvvParser.decode(item))
    }
  }).join('')

  if (html.endsWith('<br>')) {
    return html
  } else {
    return html + '<br>'
  }
}

export function convertHtmlToSmartString(html: string | null | undefined) {
  html ??= ''

  if (html.endsWith('<br>')) {
    html = html.slice(0, -4)
  }

  return html.split(SPAN_REGEXP).map((item) => {
    if (SPAN_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(SPAN_REGEXP_WITH_CAPTURES)!
      return createField(value, defaultValue)
    } else {
      return AvvParser.encode(decodeHTML(item))
    }
  }).join('')
}

export function convertAutoNameToSmartString(autoName: string | null | undefined) {
  autoName ??= ''

  if (autoName.endsWith('<br>')) {
    autoName = autoName.slice(0, -4)
  }

  return autoName.replaceAll('\u200B', '').split(AUTONAME_REGEXP).map((item) => {
    if (AUTONAME_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(AUTONAME_REGEXP_WITH_CAPTURES)!
      return createField(value, defaultValue)
    } else {
      return AvvParser.encode(decodeHTML(item))
    }
  }).join('')
}

export function convertSmartStringToAutoName(smartString: string | null | undefined, appendBreakline = true) {
  const html = (smartString ?? '').split(FIELD_REGEXP).map((item) => {
    if (FIELD_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(FIELD_REGEXP_WITH_CAPTURES)!
      return `{{'Att_default': ['${value}', '${defaultValue}']}}`
    } else {
      return encodeHTML(AvvParser.decode(item))
    }
  }).join('')

  if (!appendBreakline || html.endsWith('<br>')) {
    return html
  } else {
    return html + '<br>'
  }
}

export function extractVariables(smartString: string | null | undefined) {
  return (smartString ?? '').split(FIELD_REGEXP).reduce<(Pick<V.SmartInput.Variable, 'value'> & { default: string })[]>((acc, item) => {
    if (FIELD_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(FIELD_REGEXP_WITH_CAPTURES)!
      acc.push({
        value: AvvParser.decode(value),
        default: AvvParser.decode(defaultValue)
      })
    }

    return acc
  }, [])
}

export function resolve(smartString: string, variables: Record<string, string | number>) {
  return smartString.split(FIELD_REGEXP).map((item) => {
    if (FIELD_REGEXP.test(item)) {
      const [, value, defaultValue] = item.match(FIELD_REGEXP_WITH_CAPTURES)!
      return variables[AvvParser.decode(value)] || AvvParser.decode(defaultValue)
    } else {
      return AvvParser.decode(item)
    }
  }).join('')
}
